import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  ArrowLeft, RefreshCw, BarChart2, CheckCircle, Award,
  AlertTriangle, TrendingDown, TrendingUp, ShoppingCart
} from 'lucide-react';

const BidAnalysisView = () => {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState({});
  const [creatingPO, setCreatingPO] = useState(false);

  useEffect(() => {
    fetchDataAndAnalyze();
  }, [rfqId]);

  const fetchDataAndAnalyze = async () => {
    try {
      setLoading(true);
      const [rfqResponse, quotesResponse] = await Promise.all([
        apiClient.get(`/api/rfq-workflow/rfq/${rfqId}`),
        apiClient.get(`/api/rfq-workflow/quotes/${rfqId}`)
      ]);

      if (rfqResponse?.rfq) {
        setRfq(rfqResponse.rfq);
      }
      if (quotesResponse?.quotes) {
        setQuotes(quotesResponse.quotes);
      }

      // Auto-run AI analysis if there are quotes
      if (quotesResponse?.quotes?.length > 0) {
        await runAnalysis(quotesResponse.quotes);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeVendorSelectionFromAnalysis = (analysisResult, quotesData) => {
    // Use AI recommendations for initial selection
    const selection = {};

    if (analysisResult?.recommendations) {
      analysisResult.recommendations.forEach(rec => {
        if (rec.item_id && rec.recommendation?.vendor_id) {
          selection[rec.item_id] = rec.recommendation.vendor_id;
        }
      });
    }

    // Fallback to lowest price for items without AI recommendation
    const itemQuotes = {};
    quotesData.forEach(quote => {
      if (!itemQuotes[quote.item_id]) {
        itemQuotes[quote.item_id] = [];
      }
      itemQuotes[quote.item_id].push(quote);
    });

    Object.entries(itemQuotes).forEach(([itemId, quotes]) => {
      if (!selection[itemId] && quotes.length > 0) {
        const lowestQuote = quotes.reduce((min, q) =>
          q.unit_price < min.unit_price ? q : min
        );
        selection[itemId] = lowestQuote.vendor_id;
      }
    });

    setSelectedVendors(selection);
  };

  const runAnalysis = async (quotesData = null) => {
    try {
      setAnalyzing(true);
      const response = await apiClient.post('/api/rfq-workflow/analyze', {
        rfq_id: rfqId
      });

      if (response) {
        setAnalysis(response);
        // Initialize vendor selection from AI recommendations
        initializeVendorSelectionFromAnalysis(response, quotesData || quotes);
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      // Fallback to lowest price selection if AI analysis fails
      if (quotesData || quotes.length > 0) {
        const data = quotesData || quotes;
        const selection = {};
        const itemQuotes = {};
        data.forEach(quote => {
          if (!itemQuotes[quote.item_id]) {
            itemQuotes[quote.item_id] = [];
          }
          itemQuotes[quote.item_id].push(quote);
        });
        Object.entries(itemQuotes).forEach(([itemId, q]) => {
          const lowestQuote = q.reduce((min, quote) =>
            quote.unit_price < min.unit_price ? quote : min
          );
          selection[itemId] = lowestQuote.vendor_id;
        });
        setSelectedVendors(selection);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const selectVendor = (itemId, vendorId) => {
    setSelectedVendors(prev => ({
      ...prev,
      [itemId]: vendorId
    }));
  };

  const createPurchaseOrders = async () => {
    try {
      setCreatingPO(true);
      const response = await apiClient.post('/api/rfq-workflow/generate-po', {
        rfq_id: rfqId,
        item_vendor_map: selectedVendors
      });

      if (response?.success) {
        alert('Purchase orders created successfully!');
        navigate(`/rfq-workflow/rfq/${rfqId}`);
      }
    } catch (error) {
      console.error('Error creating POs:', error);
      alert('Failed to create purchase orders: ' + (error.message || 'Unknown error'));
    } finally {
      setCreatingPO(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Group quotes by item
  const quotesByItem = quotes.reduce((acc, quote) => {
    if (!acc[quote.item_id]) {
      acc[quote.item_id] = {
        itemName: quote.item_name || quote.item_id,
        requestedQuantity: quote.requested_quantity,
        quotes: []
      };
    }
    acc[quote.item_id].quotes.push(quote);
    return acc;
  }, {});

  // Calculate totals
  const calculateTotals = () => {
    let total = 0;
    Object.entries(selectedVendors).forEach(([itemId, vendorId]) => {
      const itemQuotes = quotesByItem[itemId]?.quotes || [];
      const selectedQuote = itemQuotes.find(q => q.vendor_id === vendorId);
      if (selectedQuote) {
        total += selectedQuote.total_price || (selectedQuote.unit_price * selectedQuote.quantity);
      }
    });
    return total;
  };

  if (loading) {
    return (
      <MainLayout title="Bid Analysis">
        <div className="flex flex-col justify-center items-center h-64 gap-4">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
          <p className="text-gray-500 dark:text-gray-400">
            {analyzing ? 'Running AI Analysis...' : 'Loading quotes...'}
          </p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Bid Analysis">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}`)}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Bid Analysis
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {rfq ? `RFQ: ${rfq.rfq_number} - ${rfq.rfq_title}` : ''}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => runAnalysis()}
              disabled={analyzing || quotes.length === 0}
            >
              {analyzing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BarChart2 className="w-4 h-4 mr-2" />
              )}
              {analysis ? 'Re-run AI Analysis' : 'Run AI Analysis'}
            </Button>
          </div>
        </div>

        {/* AI Analysis Results */}
        {analysis && (
          <Card className="border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                <BarChart2 className="w-5 h-5" />
                AI Analysis Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analysis.summary && (
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Summary</h4>
                    <p className="text-gray-600 dark:text-gray-400">{analysis.summary}</p>
                  </div>
                )}
                {analysis.recommendations && analysis.recommendations.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Item Recommendations</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {analysis.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-gray-600 dark:text-gray-400">
                          <strong>{rec.item_name}</strong>: {rec.recommendation ? (
                            <>Best vendor is <span className="text-purple-600 font-medium">{rec.recommendation.vendor_name}</span> at {formatCurrency(rec.recommendation.unit_price)}{rec.recommendation.lead_time_days ? ` (${rec.recommendation.lead_time_days} days delivery)` : ''} - Score: {rec.recommendation.score?.toFixed(0)}%</>
                          ) : 'No quotes received'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.savings_potential && (
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <TrendingDown className="w-5 h-5" />
                    <span>Potential Savings: {formatCurrency(analysis.savings_potential)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quotes by Item */}
        {quotes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Quotes Received
              </h3>
              <p className="text-gray-500 mb-4">
                There are no vendor quotes to analyze for this RFQ yet.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}/responses`)}
              >
                Check Vendor Responses
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {Object.entries(quotesByItem).map(([itemId, itemData]) => (
              <Card key={itemId}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{itemData.itemName}</span>
                    <span className="text-sm font-normal text-gray-500">
                      Requested: {itemData.requestedQuantity} units
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Select</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Unit Price</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Lead Time</TableHead>
                        <TableHead>AI Score</TableHead>
                        <TableHead>Comparison</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemData.quotes
                        .sort((a, b) => a.unit_price - b.unit_price)
                        .map((quote, idx) => {
                          const isLowest = idx === 0;
                          const isSelected = selectedVendors[itemId] === quote.vendor_id;
                          const avgPrice = itemData.quotes.reduce((sum, q) => sum + q.unit_price, 0) / itemData.quotes.length;
                          const priceDiff = ((quote.unit_price - avgPrice) / avgPrice) * 100;

                          // Check if this vendor is AI recommended
                          const aiRec = analysis?.recommendations?.find(r => r.item_id === itemId);
                          const isAIRecommended = aiRec?.recommendation?.vendor_id === quote.vendor_id;

                          return (
                            <TableRow
                              key={quote.quote_id}
                              className={isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                            >
                              <TableCell>
                                <input
                                  type="radio"
                                  name={`vendor-${itemId}`}
                                  checked={isSelected}
                                  onChange={() => selectVendor(itemId, quote.vendor_id)}
                                  className="w-4 h-4 text-blue-600"
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {quote.vendor_name}
                                  {isAIRecommended && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                                      <Award className="w-3 h-3" />
                                      AI Recommended
                                    </span>
                                  )}
                                  {isLowest && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                      Lowest Price
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{formatCurrency(quote.unit_price)}</TableCell>
                              <TableCell>{quote.quantity_offered || quote.quantity || '-'}</TableCell>
                              <TableCell className="font-medium">
                                {formatCurrency(quote.total_price || (quote.unit_price * (quote.quantity_offered || quote.quantity || 1)))}
                              </TableCell>
                              <TableCell>
                                {quote.lead_time_days ? `${quote.lead_time_days} days` : '-'}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const itemRec = analysis?.recommendations?.find(r => r.item_id === itemId);
                                  const vendorScore = itemRec?.all_quotes?.find(q => q.vendor_id === quote.vendor_id);
                                  if (vendorScore?.score) {
                                    const score = vendorScore.score;
                                    return (
                                      <span className={`font-medium ${
                                        score >= 70 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600'
                                      }`}>
                                        {score.toFixed(0)}%
                                      </span>
                                    );
                                  }
                                  return '-';
                                })()}
                              </TableCell>
                              <TableCell>
                                <div className={`flex items-center gap-1 ${
                                  priceDiff < 0 ? 'text-green-600' : priceDiff > 0 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {priceDiff < 0 ? (
                                    <TrendingDown className="w-4 h-4" />
                                  ) : priceDiff > 0 ? (
                                    <TrendingUp className="w-4 h-4" />
                                  ) : null}
                                  {Math.abs(priceDiff).toFixed(1)}% {priceDiff < 0 ? 'below' : 'above'} avg
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}

            {/* Summary & Actions */}
            <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                      Selection Summary
                    </h3>
                    <p className="text-sm text-gray-500">
                      {Object.keys(selectedVendors).length} items with vendors selected
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Estimated Total</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(calculateTotals())}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}`)}
              >
                Cancel
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={createPurchaseOrders}
                disabled={creatingPO || Object.keys(selectedVendors).length === 0}
              >
                {creatingPO ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                Create Purchase Orders
              </Button>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default BidAnalysisView;
